//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { CSSProperties } from 'react';
import { createTheme } from '@mui/material/styles';
import { alpha, darken, lighten } from '@mui/system';
import '@fontsource/barlow-semi-condensed';
import '@fontsource/roboto-mono';
import '@fontsource/rubik';
import '@fontsource/source-code-pro';
import redWhiteFlatLogo from '@/images/logo/red_white_mc_sparkle.svg';
import whiteFlatLogo from '@/images/logo/white_flat.svg';

// ---------------------------------------------------------------------------
// Color palette types
// ---------------------------------------------------------------------------

interface ColorShades {
  p10: string;
  p20: string;
  p30: string;
  p40: string;
  p50: string;
  p60: string;
  p70: string;
  p80: string;
  p90: string;
}

interface GrayScale {
  blackHole: string;
  nearBlack: string;
  astral1: string;
  astral2: string;
  astral3: string;
  astral4: string;
  nebula1: string;
  nebula2: string;
  nebula3: string;
  nearWhite: string;
  light: string;
}

interface PrimaryColors {
  rocketRed: string;
  stellarBlue: string;
  solarFlare: string;
}

interface AccentColors {
  deepSpace: string;
  ultraViolet: string;
  celestial: string;
  infrared: string;
}

interface ThemeColors {
  primary: PrimaryColors;
  accent: AccentColors;
  grays: GrayScale;
  rocketRed: ColorShades;
  stellarBlue: ColorShades;
  solarFlare: ColorShades;
  orbital: ColorShades;
  velocity: ColorShades;
  velocitySky: ColorShades;
  trajectory: ColorShades;
  colony: ColorShades;
  solarMax: ColorShades;
  earth: ColorShades;
}

// ---------------------------------------------------------------------------
// Custom theme property types
// ---------------------------------------------------------------------------

interface ThemeBorder {
  default: string;
  darker: string;
  thick: string;
  radius: string;
}

interface ThemeLogo {
  home: string;
  menu: string;
  icon: string;
}

interface ThemeMenuItem {
  textTransform: string;
}

// ---------------------------------------------------------------------------
// Custom palette types
// ---------------------------------------------------------------------------

interface MenuPanelState {
  text: string;
  background: string;
}

interface PaletteMenuPanel {
  default: MenuPanelState;
  hover: MenuPanelState;
  selected: MenuPanelState;
  disabled: MenuPanelState;
}

interface PaletteInfoPanel {
  text: string;
  background: string;
}

interface PanelState {
  text: string;
  background: string;
}

interface PalettePanel {
  default: PanelState;
  hover: PanelState;
  selected: PanelState;
}

interface PaletteCharts {
  default: string;
  secondary: string;
}

interface PaletteChatbot {
  background: string;
  user: string;
  code: string;
}

interface PaletteDataset {
  background: string;
}

interface PaletteData {
  main: string;
}

interface SchemaGraphElement {
  normal: string;
  hover: string;
  text?: string;
}

interface PaletteSchemaGraph {
  node: SchemaGraphElement;
  edge: SchemaGraphElement;
}

interface PaletteJobs {
  scheduled: string;
  running: string;
  suspended: string;
  blocked: string;
  completed: string;
  canceled: string;
  failed: string;
  rollback: string;
  unknown_job_status: string;
}

interface PaletteLogs {
  IO: string;
  Error: string;
}

interface PaletteBorder {
  default: string;
  darker: string;
}

interface PaletteHeader {
  background: string;
}

interface PaletteHistory {
  favorite: string;
}

interface PaletteResultTable {
  node: string;
  edge: string;
  both: string;
}

// Shared chrome for data tables (header background, etc.) so the various
// tables look consistent.  Kept distinct from action.hover so a hovered row
// never matches the header.
interface PaletteTable {
  headerBg: string;
  headerText: string;
}

// ---------------------------------------------------------------------------
// MUI module augmentations
// ---------------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface Theme {
    colors: ThemeColors;
    border: ThemeBorder;
    logo: ThemeLogo;
    menuItem: ThemeMenuItem;
  }
  interface ThemeOptions {
    colors?: ThemeColors;
    border?: ThemeBorder;
    logo?: ThemeLogo;
    menuItem?: ThemeMenuItem;
  }

  interface BreakpointOverrides {
    smMd: true;
  }

  interface TypographyVariants {
    code: CSSProperties;
  }
  interface TypographyVariantsOptions {
    code?: CSSProperties;
  }

  interface Palette {
    menuPanel: PaletteMenuPanel;
    infoPanel: PaletteInfoPanel;
    panel: PalettePanel;
    charts: PaletteCharts;
    chatbot: PaletteChatbot;
    dataset: PaletteDataset;
    data: PaletteData;
    schemaGraph: PaletteSchemaGraph;
    nodeColors: string[];
    edgeColors: string[];
    jobs: PaletteJobs;
    logs: PaletteLogs;
    border: PaletteBorder;
    header: PaletteHeader;
    history: PaletteHistory;
    resultTable: PaletteResultTable;
    table: PaletteTable;
    // Row/region background previewing the items a pending destructive action
    // (e.g. a bulk cancel/drop) would affect, and the matching border used to
    // bound that region.
    affectedHighlight: string;
    affectedHighlightBorder: string;
  }
  interface PaletteOptions {
    menuPanel?: PaletteMenuPanel;
    infoPanel?: PaletteInfoPanel;
    panel?: PalettePanel;
    charts?: PaletteCharts;
    chatbot?: PaletteChatbot;
    dataset?: PaletteDataset;
    data?: PaletteData;
    schemaGraph?: PaletteSchemaGraph;
    nodeColors?: string[];
    edgeColors?: string[];
    jobs?: PaletteJobs;
    logs?: PaletteLogs;
    border?: PaletteBorder;
    header?: PaletteHeader;
    history?: PaletteHistory;
    resultTable?: PaletteResultTable;
    table?: PaletteTable;
    affectedHighlight?: string;
    affectedHighlightBorder?: string;
  }

}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    code: true;
  }
}

// ---------------------------------------------------------------------------
// Color definitions
// ---------------------------------------------------------------------------

const colors: ThemeColors = {
  primary: {
    rocketRed: '#F2253E',
    stellarBlue: '#424AFB',
    solarFlare: '#FCA409',
  },
  accent: {
    deepSpace: '#1D253C',
    ultraViolet: '#4E44A4',
    celestial: '#7E95F4',
    infrared: '#8B3C99',
  },
  grays: {
    blackHole: '#000000',
    nearBlack: '#111317',
    astral1: '#29292D',
    astral2: '#4B4C53',
    astral3: '#6C6D78',
    astral4: '#BFBFCC',
    nebula1: '#D9D9E3',
    nebula2: '#EAEAEF',
    nebula3: '#F8F9F9',
    nearWhite: '#FCFCFF',
    light: '#FFFFFF',
  },
  rocketRed: {
    p10: '#FDDCE0',
    p20: '#FBB9C1',
    p30: '#F996A2',
    p40: '#F55165',
//    p50: '#F2253E',
    p50: '#E81B34',  // This fixes the contrast issues.
    p60: '#C21E32',
    p70: '#74121E',
    p80: '#4D0C14',
    p90: '#27060A',
  },
  stellarBlue: {
    p10: '#E1E2FE',
    p20: '#C3C5FE',
    p30: '#A4A8FD',
    p40: '#686EFC',
    p50: '#424AFB',
    p60: '#353BC9',
    p70: '#202478',
    p80: '#151850',
    p90: '#0B0C28',
  },
  solarFlare: {
    p10: '#FFF0D8',
    p20: '#FEE2B0',
    p30: '#FED389',
    p40: '#FDB63A',
    p50: '#FCA409',
    p60: '#CA8307',
    p70: '#794F04',
    p80: '#513403',
    p90: '#281A01',
  },
  orbital: {
    p10: '#E1DFF1',
    p20: '#C3BFE3',
    p30: '#A59FD6',
    p40: '#695FBA',
    p50: '#4338A9',
    p60: '#362C87',
    p70: '#201B51',
    p80: '#161236',
    p90: '#0B091B',
  },
  velocity: {
    p10: '#DCE3F2',
    p20: '#BAC7E6',
    p30: '#97ABD9',
    p40: '#5174C0',
    p50: '#2651B0',
    p60: '#1E418D',
    p70: '#122754',
    p80: '#0C1A38',
    p90: '#060D1C',
  },
  velocitySky: {
    p10: '#DAE8FB',
    p20: '#B6D0F7',
    p30: '#91B9F3',
    p40: '#488AEB',
    p50: '#1A6DE6',
    p60: '#1557B8',
    p70: '#0C346E',
    p80: '#08234A',
    p90: '#041125',
  },
  trajectory: {
    p10: '#E8DFEA',
    p20: '#D1BED4',
    p30: '#BA9EBF',
    p40: '#8C5D94',
    p50: '#6F347A',
    p60: '#592A61',
    p70: '#35193A',
    p80: '#241127',
    p90: '#120813',
  },
  colony: {
    p10: '#DEEDE0',
    p20: '#BDDBC0',
    p30: '#9DC9A1',
    p40: '#5BA563',
//    p50: '#328E3B',
    p50: '#2B8734',  // This fixes the contrast issues.
    p60: '#287230',
    p70: '#18441D',
    p80: '#102E13',
    p90: '#08170A',
  },
  solarMax: {
    p10: '#FFE5D8',
    p20: '#FECBB0',
    p30: '#FEB289',
    p40: '#FD7E3A',
    p50: '#FC5E09',
    p60: '#CA4B07',
    p70: '#792D04',
    p80: '#511E03',
    p90: '#280F01',
  },
  earth: {
    p10: '#D6EBF0',
    p20: '#ADD7E2',
    p30: '#85C3D3',
    p40: '#339CB6',
//    p50: '#0083A4',
    p50: '#0081a2',  // This fixes the contrast issues.
    p60: '#006983',
    p70: '#003F4E',
    p80: '#002A34',
    p90: '#00151A',
  },
};

const primary = {
  main: colors.grays.astral3,
  dark: darken(colors.grays.astral3, 0.2),
  light: lighten(colors.grays.astral3, 0.1),
  contrastText: colors.grays.nearWhite,
};

const secondary = {
  main: colors.stellarBlue.p50,
  dark: colors.stellarBlue.p60,
  light: colors.stellarBlue.p40,
  contrastText: colors.grays.nearWhite,
};

const error = {
  main: colors.rocketRed.p50,
  dark: colors.rocketRed.p60,
  light: colors.rocketRed.p40,
  contrastText: colors.grays.light,
};

const warning = {
  main: colors.solarFlare.p50,
  dark: colors.solarFlare.p60,
  light: colors.solarFlare.p40,
  contrastText: colors.grays.light,
};

const success = {
  main: colors.colony.p50,
  dark: colors.colony.p60,
  light: colors.colony.p40,
  contrastText: colors.grays.light,
};

const info = {
  main: colors.velocity.p50,
  dark: colors.velocity.p60,
  light: colors.velocity.p40,
  contrastText: colors.grays.light,
};

const baseTheme = createTheme();

export const rocketGraphTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      smMd: 800,  // Custom breakpoint between sm and md
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  colors: colors,
  typography: {
    ...baseTheme.typography,
    h1: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    h4: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    h5: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    h6: {
      fontFamily: "'Barlow Semi Condensed', sans-serif",
      textTransform: 'uppercase',
    },
    subtitle1: {
      fontFamily: "'Rubik', sans-serif",
    },
    subtitle2: {
      fontFamily: "'Rubik', sans-serif",
    },
    body1: {
      fontFamily: "'Rubik', sans-serif",
    },
    body2: {
      fontFamily: "'Rubik', sans-serif",
    },
    button: {
      textTransform: 'uppercase',
    },
    fontFamily: "'Rubik', sans-serif",
    code: {
      ...baseTheme.typography.body2,
      fontFamily: '\'Roboto Mono\', \'Source Code Pro\', Menlo, Monaco, Consolas, \'Courier New\', monospace',
    },
  },
  palette: {
    ...baseTheme.palette,
    primary: primary,
    secondary: secondary,
    error: error,
    warning: warning,
    success: success,
    info: info,
    // Subtle brand-red tint for previewing rows a pending destructive bulk
    // action would affect (error.main is the brand "rocketRed"), with a
    // fainter border to bound the affected block.
    affectedHighlight: alpha(error.main, 0.18),
    affectedHighlightBorder: alpha(error.main, 0.10),
    action: {
      ...baseTheme.palette.action,
      // The hover every MUI component and list reads, sourced from the
      // palette rather than left at MUI's default.  Translucent, so it
      // reads over a tinted panel and over the header alike; over white it
      // renders #F4F4F4, a hair darker than MUI's 4% black at #F5F5F5.
      hover: alpha(colors.grays.astral1, 0.05),
    },
    background: {
      default: colors.grays.light,
      paper: colors.grays.light,
    },
    text: {
      primary: colors.grays.nearBlack,
      secondary: colors.grays.astral3,
    },
    menuPanel: {
      default: {
        text: colors.grays.light,
        background: colors.grays.nearBlack,
      },
      hover: {
        text: colors.grays.light,
        background: colors.rocketRed.p50,
      },
      selected: {
        text: colors.grays.light,
        background: colors.grays.astral2,
      },
      disabled: {
        text: colors.grays.astral3,
        background: colors.grays.nearBlack,
      },
    },
    infoPanel: {
      text: colors.grays.light,
      background: colors.grays.astral2
    },
    panel: {
      default: {
        text: colors.grays.astral3,
        background: colors.grays.light,
      },
      hover: {
        text: colors.stellarBlue.p50,
        background: colors.grays.nebula1,
      },
      selected: {
        text: colors.stellarBlue.p50,
        background: colors.grays.nebula2,
      },
    },
    charts: {
      default: colors.velocity.p40,
      secondary: colors.solarFlare.p40,
    },
    chatbot: {
      background: colors.grays.light,
      user: colors.velocitySky.p10,
      code: colors.grays.nebula2,
    },
    dataset: {
      background: colors.grays.nebula3,
    },
    data: {
      main: colors.velocity.p50,
    },
    schemaGraph: {
      node: {
        normal: colors.rocketRed.p50,
        hover: colors.rocketRed.p30,
        text: colors.grays.nearBlack,
      },
      edge: {
        normal: colors.grays.astral4,
        hover: colors.grays.nearBlack,
      },
    },
    nodeColors: [
      colors.rocketRed.p50,
      colors.stellarBlue.p50,
      colors.solarFlare.p50,
      colors.orbital.p50,
      colors.velocity.p50,
      colors.trajectory.p50,
      colors.colony.p50,
      colors.solarMax.p50,
      colors.earth.p50,
      colors.rocketRed.p70,
      colors.stellarBlue.p70,
      colors.solarFlare.p70,
      colors.orbital.p70,
      colors.velocity.p70,
      colors.trajectory.p70,
      colors.colony.p70,
      colors.solarMax.p70,
      colors.earth.p70,
    ],
    edgeColors: [
      colors.grays.nearBlack,
      colors.grays.astral2,
      colors.grays.astral4,
      colors.grays.nebula2,
      colors.rocketRed.p80,
      colors.stellarBlue.p80,
      colors.solarFlare.p80,
      colors.orbital.p80,
      colors.velocity.p80,
      colors.trajectory.p80,
      colors.colony.p80,
      colors.solarMax.p80,
      colors.earth.p80,
    ],
    jobs: {
      scheduled: colors.grays.astral3,
      running: colors.velocity.p50,
      suspended: colors.solarFlare.p60,
      blocked: colors.orbital.p50,
      completed: success.main,
      canceled: colors.solarMax.p60,
      failed: error.main,
      rollback: colors.trajectory.p60,
      unknown_job_status: colors.earth.p50,
    },
    logs: {
      IO: colors.velocity.p10,
      Error: colors.rocketRed.p10,
    },
    border: {
      default: colors.grays.astral4,
      darker: colors.grays.astral3,
    },
    header: {
      background: colors.rocketRed.p50,
    },
    history: {
      favorite: colors.solarFlare.p30,
    },
    resultTable: {
      node: colors.trajectory.p10,
      edge: colors.colony.p10,
      both: colors.velocity.p10,
    },
    table: {
      headerBg: colors.grays.nebula2,
      headerText: colors.grays.nearBlack,
    },
  },
  menuItem: {
    textTransform: 'uppercase',
  },
  border: {
    default: `1px solid ${colors.grays.astral4}`,
    darker: `1px solid ${colors.grays.astral3}`,
    thick: `2px solid ${colors.grays.astral4}`,
    radius: '8px',
  },
  logo: {
    home: whiteFlatLogo,
    menu: redWhiteFlatLogo,
    icon: '/favicon.svg',
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          code: 'p',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: 'contained', color: 'primary' },
              style: {
                backgroundColor: error.main,
                color: error.contrastText,
                '&:hover': {
                  backgroundColor: error.dark,
                },
              },
            },
          ],
        },
      },
    },
    MuiTooltip: {
      // A tooltip waits before it opens, and waits again for the next one.
      //
      // MUI's own rule is that a tooltip already shown makes the following one instant, on the
      // grounds that somebody reading tooltips wants the next.  Down a list that is the
      // opposite: one tooltip read, and every row the pointer crosses on its way elsewhere
      // opens its own under the cursor.  The pause is the whole of what makes a tooltip an
      // answer to a question rather than an interruption, so it is asked for each time.
      defaultProps: {
        enterDelay: 400,
        enterNextDelay: 400,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.grays.nebula2,
          color: colors.grays.nearBlack,
        },
        arrow: {
          color: colors.grays.nebula2,
        },
      },
    },
    /*
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },*/
  },
  shape: {
    borderRadius: 8,
  }
});

export const rocketGraphGlobalStyles = {
  body: {
    margin: 0,
    fontFamily: "'Rubik', sans-serif",
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  header: {
    position: 'sticky',
    top: '0',
    zIndex: '1100', // Above most but allow high priority things on top.
  },
  main: {
    backgroundColor: colors.grays.light,
  },
  footer: {
    backgroundColor: colors.grays.light,
    bottom: '0',
    zIndex: '1100', // Above most but allow high priority things on top.
  },
  code: {
    fontFamily: '\'Roboto Mono\', \'Source Code Pro\', Menlo, Monaco, Consolas, \'Courier New\', monospace',
  },
  // Code mirror.
  '.cm-content': {
    fontFamily: '\'Roboto Mono\', \'Source Code Pro\', Menlo, Monaco, Consolas, \'Courier New\', monospace',
  },
//  '.clear': {
//    '&::before': {
//      content: '""',
//      all: 'unset',
//    },
//  },
  h1: {
    fontFamily: "'Barlow Semi Condensed', sans-serif",
    textTransform: 'uppercase',
    margin: '2px',
    padding: '2px',
  },
  h2: {
    fontFamily: "'Barlow Semi Condensed', sans-serif",
    textTransform: 'uppercase',
    color: rocketGraphTheme.palette.text.primary,
  },
};
